import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  FileText, 
  AlertTriangle,
  ExternalLink,
  ChevronRight,
  Car,
  Calendar,
  DollarSign,
  Clock,
  Download,
  Phone,
  Mail,
  HelpCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Gift,
  ArrowRight,
  BookOpen,
  Lock,
  Eye,
  EyeOff,
  Sparkles,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  User,
  X,
  CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';
import { getUserFriendlyError } from '../utils/errorMessages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency formatting
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num % 1 === 0) {
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function PortalDashboardPage() {
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [claimsCount, setClaimsCount] = useState(0);
  
  // Password setup modal state
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Coverage breakdown modal state
  const [showCoverageModal, setShowCoverageModal] = useState(false);

  // Format date as MM/DD/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  useEffect(() => {
    // Small delay to ensure localStorage is fully available after redirect
    const initDashboard = () => {
      console.log('[PortalDashboard] Initializing dashboard...');
      const token = getToken();
      console.log('[PortalDashboard] Token from localStorage:', !!token);
      
      if (!token) {
        console.log('[PortalDashboard] No token found, redirecting to login');
        navigate('/portal/login');
        return;
      }
      
      console.log('[PortalDashboard] Token found, fetching dashboard data...');
      fetchDashboard(token);
    };
    
    // Give localStorage a moment to sync after page navigation
    const timer = setTimeout(initDashboard, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboard = async (token) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[PortalDashboard] Fetching dashboard with token:', token?.substring(0, 20) + '...');
      
      const response = await fetch(`${API_URL}/api/portal/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[PortalDashboard] Dashboard response status:', response.status);
      
      if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[PortalDashboard] 401 Unauthorized:', errorData);
        await logout();
        navigate('/portal/login');
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          throw new Error('No policies found for your account. If you recently purchased, please allow a few minutes for processing.');
        } else if (response.status >= 500) {
          throw new Error('Our servers are temporarily unavailable. Please try again in a moment.');
        } else {
          throw new Error(errorData.detail || 'Failed to load dashboard. Please try again.');
        }
      }
      
      const data = await response.json();
      setDashboardData(data);
      setSelectedPolicy(data.primary_policy);
      
      // Fetch claims count
      try {
        const claimsResponse = await fetch(`${API_URL}/api/claims/summary`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (claimsResponse.ok) {
          const claimsData = await claimsResponse.json();
          setClaimsCount(claimsData.summary?.total || 0);
        }
      } catch (claimsErr) {
        console.log('[PortalDashboard] Could not fetch claims count:', claimsErr);
      }
      
      // Check if user needs to set up password
      if (data.user?.needs_password_setup) {
        setShowPasswordSetup(true);
      }
      
    } catch (err) {
      setError(getUserFriendlyError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const isActivePolicy = (status) => {
    const s = status?.toLowerCase();
    return s === 'active' || s === 'issued' || s === 'policy_issued_complete' || s === 'policy_issued';
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'active' || s === 'issued' || s === 'policy_issued_complete' || s === 'policy_issued') {
      return 'bg-green-500 text-white'; // Active = Green
    }
    if (s === 'pending' || s === 'pending_payment' || s === 'processing') {
      return 'bg-yellow-500 text-white'; // Pending = Yellow
    }
    if (s === 'cancelled' || s === 'expired' || s === 'canceled' || s === 'inactive') {
      return 'bg-red-500 text-white'; // Cancelled/Expired = Red
    }
    return 'bg-slate-500 text-white'; // Default
  };

  const getStatusLabel = (status) => {
    const s = status?.toLowerCase();
    if (s === 'active' || s === 'issued' || s === 'policy_issued_complete' || s === 'policy_issued') {
      return 'Active';
    }
    if (s === 'pending' || s === 'pending_payment' || s === 'processing') {
      return 'Pending';
    }
    if (s === 'cancelled' || s === 'canceled') {
      return 'Cancelled';
    }
    if (s === 'expired') {
      return 'Expired';
    }
    if (s === 'inactive') {
      return 'Inactive';
    }
    return status || 'Unknown';
  };

  // Password validation
  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(pwd)) return 'Password must contain at least one number';
    return null;
  };

  // Handle password setup submission
  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    // Validate password
    const validationError = validatePassword(password);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    
    // Check passwords match
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setSettingPassword(true);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/setup-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: password,
          confirm_password: confirmPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to set password');
      }
      
      setPasswordSuccess(true);
      
      // Close modal after success message
      setTimeout(() => {
        setShowPasswordSetup(false);
        setPasswordSuccess(false);
        // Update local state so modal doesn't show again
        if (dashboardData?.user) {
          setDashboardData({
            ...dashboardData,
            user: { ...dashboardData.user, needs_password_setup: false }
          });
        }
      }, 2000);
      
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setSettingPassword(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#3B5BDB] mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center px-4">
        <Card className="max-w-md w-full shadow-xl border-0 rounded-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => fetchDashboard(getToken())} 
                className="w-full bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
                data-testid="dashboard-retry-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/portal/login')} 
                className="w-full rounded-xl"
                data-testid="dashboard-back-login-btn"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const vehicleInfo = selectedPolicy?.vehicle || {};
  const premiumPaid = selectedPolicy?.premium_paid || 0;
  const renewalInfo = dashboardData?.renewal_info || selectedPolicy?.renewal;
  const daysRemaining = selectedPolicy?.days_remaining || 0;
  const userName = dashboardData?.policyholder?.first_name || dashboardData?.user?.name?.split(' ')[0] || 'Policyholder';

  return (
    <PortalLayout 
      pageTitle="Dashboard" 
      policyNumber={selectedPolicy?.policy_number}
      userName={userName}
      claimsCount={claimsCount}
    >
      <div className="bg-[#E8EEFF] min-h-full py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          {/* Welcome Section */}
          <div className="mb-8">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-sm font-medium text-[#3B5BDB] mb-4">
                <span className="w-2 h-2 bg-[#3B5BDB] rounded-full"></span>
                Policyholder Dashboard
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                Welcome back, <span className="text-[#3B5BDB]">{userName}</span>
              </h1>
              <p className="text-slate-600 text-lg">
                Manage your Diminished Value Protection policy
              </p>
            </div>
          </div>

          {/* Renewal Banner */}
          {renewalInfo?.eligible_for_renewal && (
            <div className="mb-8 bg-gradient-to-r from-[#3B5BDB] to-[#5B7FFF] rounded-2xl p-6 text-white shadow-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Gift className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Time to Renew Your Coverage</h3>
                    <p className="text-white/90 text-sm">
                      Your policy expires on {formatDate(renewalInfo.expiration_date)}. Renew now and save {renewalInfo.discount_percentage}% with your loyalty discount!
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/renewal?policy=${selectedPolicy?.policy_number}`)}
                  className="bg-white text-[#3B5BDB] hover:bg-white/90 rounded-xl font-semibold"
                >
                  Renew Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Column - Policy Overview */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Policy Card */}
              <Card className="shadow-xl border-0 rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-[#3B5BDB] to-[#5B7FFF] text-white px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-white/80" />
                    <span className="font-semibold">Diminished Value Protection</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {isActivePolicy(selectedPolicy?.status) && (
                      <span className="text-xs text-white/80 hidden sm:inline">
                        {daysRemaining} days left
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedPolicy?.status)}`}>
                      {getStatusLabel(selectedPolicy?.status)}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  {/* Policy Number */}
                  <div className="mb-6">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Policy Number</p>
                    <p className="text-xl font-mono font-bold text-slate-900" data-testid="policy-number">
                      {selectedPolicy?.policy_number}
                    </p>
                  </div>

                  {/* Coverage Visualization */}
                  <button 
                    onClick={() => setShowCoverageModal(true)}
                    className="w-full bg-slate-50 hover:bg-slate-100 rounded-xl p-5 mb-6 text-left transition-colors group"
                    data-testid="coverage-details-btn"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Coverage Amount</p>
                      <span className="text-xs font-medium text-[#3B5BDB] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        View Details
                        <ChevronRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-3xl font-bold text-[#3B5BDB]">
                        {formatCurrency(selectedPolicy?.coverage_amount)}
                      </span>
                      <span className="text-slate-500">maximum protection</span>
                    </div>
                    <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#3B5BDB] rounded-full" style={{ width: '100%' }}></div>
                    </div>
                    <p className="text-sm text-slate-500 mt-2">
                      Plan: {selectedPolicy?.coverage_level_name || 'Standard Coverage'}
                    </p>
                  </button>

                  {/* Policy Details Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider">Effective</span>
                      </div>
                      <p className="font-semibold text-slate-900">{formatDate(selectedPolicy?.effective_date)}</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <Clock className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider">Expires</span>
                      </div>
                      <p className="font-semibold text-slate-900">{formatDate(selectedPolicy?.expiration_date)}</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <DollarSign className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider">Deductible</span>
                      </div>
                      <p className="font-semibold text-slate-900">{formatCurrency(selectedPolicy?.deductible)}</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <DollarSign className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider">Premium Paid</span>
                      </div>
                      <p className="font-semibold text-green-600">{formatCurrency(premiumPaid)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Vehicle Card */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <Car className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Covered Vehicle</h3>
                      <p className="text-sm text-slate-500">Your protected asset</p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-5">
                    <p className="text-xl font-bold text-slate-900 mb-3">
                      {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}{vehicleInfo.trim ? ` ${vehicleInfo.trim}` : ''}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">VIN</p>
                        <p className="font-mono font-medium text-slate-700">{vehicleInfo.vin || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Body Class</p>
                        <p className="font-medium text-slate-700">{vehicleInfo.body_class || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Sidebar Cards */}
            <div className="space-y-6">
              
              {/* Policy Management - First */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Policy Management</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        navigate('/settings');
                        window.scrollTo(0, 0);
                      }}
                      className="flex items-center justify-between w-full px-4 py-3 bg-slate-50 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors"
                      data-testid="account-settings-btn"
                    >
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Account Settings
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/claims');
                        window.scrollTo(0, 0);
                      }}
                      className="flex items-center justify-between w-full px-4 py-3 bg-slate-50 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors"
                      data-testid="claims-center-btn"
                    >
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Claims Center
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/settings?tab=billing');
                        window.scrollTo(0, 0);
                      }}
                      className="flex items-center justify-between w-full px-4 py-3 bg-slate-50 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors"
                      data-testid="billing-payments-btn"
                    >
                      <span className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Billing & Payments
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions - Second */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    {/* 1. Coverage Details */}
                    <button
                      onClick={() => {
                        navigate('/coverage');
                        window.scrollTo(0, 0);
                      }}
                      className="flex items-center justify-between w-full px-4 py-3 bg-[#3B5BDB] text-white rounded-xl font-semibold hover:bg-[#2F4BC7] transition-colors"
                      data-testid="quick-coverage-details-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Coverage Details
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    
                    {/* 2. View Documents */}
                    <button
                      onClick={() => {
                        navigate(`/documents/${selectedPolicy?.policy_number}`);
                        window.scrollTo(0, 0);
                      }}
                      className="flex items-center justify-between w-full px-4 py-3 border-2 border-[#3B5BDB] text-[#3B5BDB] rounded-xl font-semibold hover:bg-[#E8EEFF] transition-colors"
                      data-testid="quick-view-documents-btn"
                    >
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        View Documents
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    
                    {/* 3. File a Claim */}
                    <button
                      onClick={() => {
                        navigate('/claims/file');
                        window.scrollTo(0, 0);
                      }}
                      className="flex items-center justify-between w-full px-4 py-3 border-2 border-[#3B5BDB] text-[#3B5BDB] rounded-xl font-semibold hover:bg-[#E8EEFF] transition-colors"
                      data-testid="quick-file-claim-btn"
                    >
                      <span className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        File a Claim
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    
                    {/* 4. Track Claims - Only show if user has filed claims */}
                    {claimsCount > 0 && (
                      <button
                        onClick={() => {
                          navigate('/claims');
                          window.scrollTo(0, 0);
                        }}
                        className="flex items-center justify-between w-full px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                        data-testid="quick-track-claims-btn"
                      >
                        <span className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4" />
                          Track Claims
                          <span className="text-xs bg-[#3B5BDB] text-white px-2 py-0.5 rounded-full">{claimsCount}</span>
                        </span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Need Help - Third */}
              <Card className="shadow-xl border-0 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#E8EEFF]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B5BDB]/10 rounded-xl flex items-center justify-center">
                      <HelpCircle className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <h3 className="font-semibold text-slate-900">Need Help?</h3>
                  </div>
                  <p className="text-slate-600 text-sm mb-4">
                    Find answers to common questions or contact our support team.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        navigate('/help');
                        window.scrollTo(0, 0);
                      }}
                      className="flex items-center justify-between w-full px-4 py-2.5 bg-white text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors border border-slate-200"
                      data-testid="help-center-btn"
                    >
                      <span className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-[#3B5BDB]" />
                        Help Center / FAQ
                      </span>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </button>
                    <a href="tel:18335677729" className="flex items-center gap-3 text-slate-700 hover:text-[#3B5BDB] transition-colors px-1">
                      <Phone className="h-4 w-4" />
                      <span className="font-medium">1-833-LOSSPAY</span>
                    </a>
                    <a href="mailto:support@losspay.com" className="flex items-center gap-3 text-slate-700 hover:text-[#3B5BDB] transition-colors px-1">
                      <Mail className="h-4 w-4" />
                      <span className="font-medium">support@losspay.com</span>
                    </a>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

      {/* Password Setup Modal - Rendered via Portal for proper z-index */}
      {showPasswordSetup && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" 
          style={{ zIndex: 9000 }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-fade-in">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Lock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Create Your Password
                  </h2>
                  <p className="text-sm text-slate-600">
                    Set up a password for quick access to your account
                  </p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handlePasswordSetup} className="p-6 space-y-4">
              {passwordSuccess ? (
                <div className="text-center py-6">
                  <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Password Set Successfully!</h3>
                  <p className="text-slate-600 text-sm">You can now log in with your email and password.</p>
                </div>
              ) : (
                <>
                  {passwordError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {passwordError}
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="new-password" className="text-slate-700 font-medium">
                      New Password
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Must be 8+ characters with uppercase, lowercase, and number
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="confirm-password" className="text-slate-700 font-medium">
                      Confirm Password
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        className="pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPasswordSetup(false)}
                      className="flex-1"
                      disabled={settingPassword}
                    >
                      Skip for Now
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                      disabled={settingPassword}
                    >
                      {settingPassword ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Setting...
                        </>
                      ) : (
                        'Set Password'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Coverage Breakdown Modal */}
      {/* Coverage Modal - Rendered via Portal for proper z-index */}
      {showCoverageModal && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" 
          style={{ zIndex: 9000 }}
          onClick={() => setShowCoverageModal(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-lg w-full shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Coverage Breakdown
                    </h2>
                    <p className="text-sm text-slate-600">
                      Understanding your protection
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCoverageModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid="close-coverage-modal"
                >
                  <XCircle className="h-5 w-5 text-slate-400" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Total Coverage */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
                <p className="text-sm text-green-700 font-medium mb-1">Total Coverage Amount</p>
                <p className="text-4xl font-bold text-green-700">
                  {formatCurrency(selectedPolicy?.coverage_amount)}
                </p>
                <p className="text-sm text-green-600 mt-2">
                  Maximum protection for your vehicle's diminished value
                </p>
              </div>

              {/* Coverage Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#3B5BDB]" />
                  What's Covered
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">Diminished Value Loss</p>
                      <p className="text-sm text-slate-600">
                        Compensation for the reduction in your vehicle's market value after an accident repair
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">Third-Party Claims</p>
                      <p className="text-sm text-slate-600">
                        Coverage when another driver is at fault and their insurance doesn't fully compensate
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">Expert Appraisal Support</p>
                      <p className="text-sm text-slate-600">
                        Access to certified appraisers to accurately assess your vehicle's diminished value
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Policy Details */}
              <div className="border-t border-slate-200 pt-4">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#3B5BDB]" />
                  Policy Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Deductible</p>
                    <p className="text-lg font-bold text-slate-900">
                      {formatCurrency(selectedPolicy?.deductible_amount || selectedPolicy?.deductible || 500)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Coverage Level</p>
                    <p className="text-lg font-bold text-slate-900">
                      {selectedPolicy?.coverage_level_name || 'Standard'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Effective Date</p>
                    <p className="text-lg font-bold text-slate-900">
                      {formatDate(selectedPolicy?.effective_date)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Expiration</p>
                    <p className="text-lg font-bold text-slate-900">
                      {formatDate(selectedPolicy?.expiration_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCoverageModal(false)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setShowCoverageModal(false);
                    navigate(`/claims/file?policy=${selectedPolicy?.policy_number}`);
                  }}
                  className="flex-1 bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                >
                  File a Claim
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
        </div>
      </div>
    </PortalLayout>
  );
}
